import React, { useEffect, useMemo, useState } from 'react';

import { useToast } from '@chakra-ui/react';
import { format as format } from 'date-fns';
import QueryString from 'qs';
import { omit } from 'ramda';
import { useQuery, useQueryClient } from 'react-query';
import { useAsyncDebounce } from 'react-table';

import emailMarketingApi from 'api/campaigns';
import { FETCH_LISTING_CAMPAIGN } from 'constants/queries';

import { EmailMarketingProps } from './EmailMarketing.props';
import EmailMarketingView from './EmailMarketing.view';

export const TABLE_OPTIONS = {
  page: 1,
  limit: 10,
};

const EmailMarketingContainer = (props: EmailMarketingProps): JSX.Element => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [tableOptions, setTableOptions] =
    useState<Record<string, any>>(TABLE_OPTIONS);

  // Parsing query parameters from the URL
  const queries = useMemo(
    () =>
      QueryString.parse(new URLSearchParams(props.location.search).toString()),
    [props.location.search]
  );

  // Reset pagination when search query changes
  useEffect(() => {
    setTableOptions((opts) => ({ ...opts, page: 1 }));
  }, [queries.search]);

  // Fetch email marketing data from the API
  const { data, refetch, isFetching } = useQuery(
    FETCH_LISTING_CAMPAIGN,
    async () =>
      emailMarketingApi.getAll(
        `${
          props.location.search ? props.location.search + '&' : '?'
        }${QueryString.stringify(tableOptions)}`
      )
  );

  // Search input debounce for optimized filtering
  const onSearchKey = useAsyncDebounce(
    (searchKey: string) =>
      props.history.push({
        pathname: props.location.pathname,
        search:
          '?' +
          new URLSearchParams({
            ...omit(['search'], queries),
            ...(searchKey ? { search: searchKey } : {}),
          }).toString(),
      }),
    600
  );

  // Navigate to email listing details
  const setListing = (emailCampaign?: any) =>
    props.history.push({
      pathname: props.location.pathname,
      search:
        '?' +
        new URLSearchParams({
          ...omit(['id'], queries),
          ...(emailCampaign ? { id: emailCampaign.id } : {}),
        }).toString(),
    });

  // Format API response data for rendering
  const emailCampaigns = useMemo(
    () =>
      (data?.data || []).map((campaign: any) => ({
        ...campaign,
        campaign_time: campaign.campaign_time
          ? format(new Date(campaign.campaign_time), 'MM/dd/y, hh:mm a')
          : '-',
        user_type:
          campaign.user_type === 'sell'
            ? 'Seller'
            : campaign.user_type === 'buy'
            ? 'Buyer'
            : campaign.user_type === 'broker'
            ? 'Agent'
            : campaign.user_type === 'all'
            ? 'All'
            : '-', // Default fallback

        createdAt: campaign.createdAt
          ? format(new Date(campaign.createdAt), 'MM/dd/y, hh:mm a')
          : '-',
      })),
    [data]
  );

  // Pagination metadata
  const meta = useMemo(
    () =>
      data?.meta || {
        count: 0,
        limit: 10,
        page: 1,
      },
    [data]
  );

  // Apply a filter
  const onSetFilter = (value: string, key: string) =>
    props.history.push({
      pathname: props.location.pathname,
      search: '?' + new URLSearchParams({ [key]: value }).toString(),
    });

  // Clear all filters
  const onClearFilter = () =>
    props.history.push({
      pathname: props.location.pathname,
    });

  // Refetch data when query parameters change
  useEffect(() => {
    queryClient.invalidateQueries([FETCH_LISTING_CAMPAIGN]);
    setTimeout(refetch, 300);
  }, [props.location.search, tableOptions]);

  const handleDelete = async (id: string, onClose: any) => {
    try {
      await emailMarketingApi.deleteCampaign(id);
      toast({
        title: 'Success',
        description: 'Campaign deleted successfully.',
        status: 'success',
        position: 'top-right',
        duration: 3000,
        isClosable: true,
      });
      refetch();
      onClose();
    } catch (error) {
      console.error('Delete Failed:', error);
    }
  };

  return (
    <EmailMarketingView
      setListing={setListing}
      data={emailCampaigns}
      meta={meta}
      onSearchKey={onSearchKey}
      isLoading={isFetching}
      onClearFilter={onClearFilter}
      onSetFilter={onSetFilter}
      tableOptions={tableOptions}
      setTableOptions={setTableOptions}
      onDelete={handleDelete}
    />
  );
};

export default EmailMarketingContainer;
